import React, { SyntheticEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgCloseButton,
  IgTabPanel,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import { Drawer, Grid, Tab, Tabs } from "@mui/material";
import { styled } from "@mui/material/styles";
import theme from "@src/theme";

import { useLoggedInUser } from "../../../api/user-auths/login";
import { usePutWidget } from "../../../api/widgets/put-widget";
import { isDrawerOpen, setDrawer } from "../../../store/slices/global";
import { selectedWidget, setWidgetName } from "../../../store/slices/widgets";
import { WidgetDrawerProps } from "../../../types/widgets";
import { mpClickSaveWidgetButton } from "../../../utils/mixpanel";

import { GlobalSettings } from "./GlobalSettings";
import { TabContent } from "./TabContent";

const PREFIX = "WidgetDrawer";

const classes = {
  title: `${PREFIX}-title`,
};

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    // width: "325px",
    borderRadius: "0px",
    padding: "20px",
    paddingLeft: "40px",
  },
  [`& .${classes.title}`]: {
    fontSize: "20px",
    fontWeight: 600,
  },
}));

export const WidgetDrawer = ({
  widgetType,
  onSave,
  isLoading,
  buttonFocus,
  isTestFlow,
}: WidgetDrawerProps) => {
  const dispatch = useDispatch();
  const isWidgetDrawerOpen = useSelector(isDrawerOpen("widgetAction"));
  const [tabValue, setTabValue] = useImmer<number>(0);
  const putWidget = usePutWidget(widgetType);
  const user = useLoggedInUser();
  const widget = useSelector(selectedWidget);

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  const handleDrawerClose = () => {
    dispatch(setDrawer({ drawer: "widgetAction", open: false }));
  };

  const onDefaultSave = () => {
    if (widget) {
      putWidget.mutate(widget, {
        onSuccess: () => {
          mpClickSaveWidgetButton(
            user.isSuccess && user.data.user.id ? user.data.user.id : null,
            widget
          );
          handleDrawerClose();
        },
      });
    }
  };

  if (!widget || !buttonFocus || buttonFocus !== widgetType) {
    return null;
  }

  return (
    <StyledDrawer
      anchor={"right"}
      open={isWidgetDrawerOpen}
      onClose={handleDrawerClose}
      sx={{ "& .MuiDrawer-paper": { width: isTestFlow ? "800px" : "400px" } }}
    >
      <Grid container direction={"column"}>
        <Grid
          container
          item
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Grid item sx={{ marginBottom: "5px" }}>
            <IgTextField
              sx={{
                width: "200px",
                "& .MuiInput-root": { fontWeight: 600, fontSize: "18px" },
              }}
              variant={"standard"}
              value={widget.name}
              onChange={(e) => {
                dispatch(setWidgetName(e.target.value));
              }}
            />
          </Grid>
          <Grid item>
            <IgCloseButton onClick={handleDrawerClose} />
          </Grid>
        </Grid>
        <GlobalSettings widget={widget} widgetType={widgetType} />
        <Grid item sx={{ marginTop: "10px", marginBottom: "10px" }}>
          <IgTypography sx={{ fontWeight: 600, fontSize: "16px" }}>
            Style Settings
          </IgTypography>
        </Grid>
        <Grid item>
          <IgTypography sx={{ fontSize: "14px" }}>
            If mobile styling is not specified then desktop styling will be
            used.
          </IgTypography>
        </Grid>
        <Grid item>
          <Tabs
            centered={true}
            value={tabValue}
            onChange={handleChange}
            aria-label="Widget Tab"
            sx={{
              borderBottom: 1,
              borderColor: theme.palette.gray.light,
              marginBottom: "20px",
            }}
          >
            <Tab sx={{ textTransform: "none" }} label="Desktop" />
            <Tab sx={{ textTransform: "none" }} label="Mobile" />
          </Tabs>
        </Grid>
        <Grid item sx={{ marginBottom: "60px" }}>
          <IgTabPanel index={0} value={tabValue}>
            <TabContent
              widgetType={widgetType}
              isTestFlow={isTestFlow}
              buttonFocus={buttonFocus}
              configType={"desktop"}
              widget={widget}
            />
          </IgTabPanel>
          <IgTabPanel index={1} value={tabValue}>
            <TabContent
              widgetType={widgetType}
              isTestFlow={isTestFlow}
              buttonFocus={buttonFocus}
              configType={"mobile"}
              widget={widget}
            />
          </IgTabPanel>
        </Grid>
        <Grid
          item
          container
          direction={"row"}
          justifyContent={"center"}
          sx={{ marginTop: "20px", marginBottom: "50px" }}
        >
          <IgButton
            loading={putWidget.isPending || isLoading}
            variant={"contained"}
            onClick={() => {
              onSave ? onSave() : onDefaultSave();
            }}
          >
            Save
          </IgButton>
        </Grid>
      </Grid>
    </StyledDrawer>
  );
};
